if (!customElements.get('nelko-product-summary')) {
  customElements.define(
    'nelko-product-summary',
    class NelkoProductSummary extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}'
        this.addButtonLabel = this.dataset.addButtonLabel || 'Add To Bag'
        this.defaultPostcode = this.dataset.defaultPostcode || ''
        this.deliveryLookupEndpoint = this.dataset.deliveryLookupEndpoint || ''
        this.deliveryDetectingText = this.dataset.deliveryDetectingText || ''
        this.deliveryUnavailableText = this.dataset.deliveryUnavailableText || ''
        this.deliveryStorageKey = 'nelko_delivery_postcode'
        this.quantityInput = this.querySelector('.quantity__input')
        this.deliveryTrigger = this.querySelector('[data-delivery-postcode-trigger]')
        this.deliveryValue = this.querySelector('[data-delivery-postcode-value]')
        this.deliveryDialog = this.querySelector('[data-delivery-dialog]')
        this.deliveryPanel = this.querySelector('[data-delivery-panel]')
        this.deliveryForm = this.querySelector('[data-delivery-form]')
        this.deliveryInput = this.querySelector('[data-delivery-postcode-input]')
        this.deliveryStatus = this.querySelector('[data-delivery-status]')
        this.deliveryDetect = this.querySelector('[data-delivery-detect]')
        this.handleDeliveryKeydownBound = this.handleDeliveryKeydown.bind(this)

        this.syncButtonLabels()
        this.bindDiscountCode()
        this.bindMobileStickyCart()
        this.bindQuantity()
        this.bindOptionButtons()
        this.bindDelivery()
        this.applySavedDeliveryPostcode()
        this.detectInitialDeliveryPostcode()
        this.syncFromVariant(this.getCurrentVariant())

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          )
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.()
        clearTimeout(this.discountCodeResetTimer)
        this.mobileSubmitObserver?.disconnect()
        this.mobileStickyVisibilityObserver?.disconnect()
        this.stickyCartResizeObserver?.disconnect()
        this.stickyCartSpacer?.remove()
        document.removeEventListener('keydown', this.handleDeliveryKeydownBound)
      }

      getCurrentVariant() {
        const variantScript = this.closest('product-info')?.querySelector(
          'variant-selects [data-selected-variant]'
        )
        if (!variantScript) return null

        try {
          return JSON.parse(variantScript.textContent)
        } catch (error) {
          return null
        }
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return
        this.quantityInput = this.querySelector('.quantity__input')
        this.restoreQuantityAfterVariantChange()
        this.syncFromVariant(event.data.variant)
      }

      bindOptionButtons() {
        this.querySelectorAll('.nelko-pdp-summary__button[data-option-position]').forEach((button) => {
          button.addEventListener('click', this.handleOptionButtonClick.bind(this))
        })
      }

      handleOptionButtonClick(event) {
        const button = event.currentTarget
        const position = parseInt(button.dataset.optionPosition || '0', 10)
        const value = button.dataset.optionValue
        const optionGroup = button.closest('.nelko-pdp-summary__option')

        if (!position || !value) return

        optionGroup?.querySelectorAll('.nelko-pdp-summary__button').forEach((optionButton) => {
          const isActive = optionButton === button
          optionButton.classList.toggle('is-active', isActive)
          optionButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        })

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects')
        const fieldset = variantSelects?.querySelectorAll('fieldset')[position - 1]
        const matchingRadio = fieldset
          ? [...fieldset.querySelectorAll('input[type="radio"]')].find((input) => input.value === value)
          : null

        if (!matchingRadio || matchingRadio.checked) return

        this.pendingQuantityValue = this.quantityInput?.value || ''
        matchingRadio.checked = true
        matchingRadio.dispatchEvent(new Event('change', { bubbles: true }))
      }

      restoreQuantityAfterVariantChange() {
        if (!this.quantityInput || !this.pendingQuantityValue) return

        const parsedQuantity = parseInt(this.pendingQuantityValue, 10)
        const minQuantity = parseInt(
          this.quantityInput.getAttribute('min') || this.quantityInput.dataset.min || '1',
          10
        )
        const maxAttribute = this.quantityInput.getAttribute('max') || this.quantityInput.dataset.max
        const maxQuantity = maxAttribute ? parseInt(maxAttribute, 10) : null

        if (!parsedQuantity || parsedQuantity < minQuantity) {
          this.quantityInput.value = String(minQuantity)
        } else if (maxQuantity && parsedQuantity > maxQuantity) {
          this.quantityInput.value = String(maxQuantity)
        } else {
          this.quantityInput.value = String(parsedQuantity)
        }

        this.pendingQuantityValue = ''
      }

      syncOptionButtonsFromVariant(variant) {
        if (!variant?.options?.length) return

        this.querySelectorAll('.nelko-pdp-summary__option[data-option-position]').forEach((optionGroup) => {
          const position = parseInt(optionGroup.dataset.optionPosition || '0', 10)
          const selectedValue = variant.options[position - 1]

          optionGroup.querySelectorAll('.nelko-pdp-summary__button').forEach((button) => {
            const isActive = button.dataset.optionValue === selectedValue
            button.classList.toggle('is-active', isActive)
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
          })
        })
      }

      syncFromVariant(variant) {
        if (!variant) return
        this.currentVariant = variant
        this.syncOptionButtonsFromVariant(variant)
        this.updateTotals()
        this.syncMobileSubmitButton()
      }

      syncButtonLabels() {
        const submitLabel = this.querySelector('.nelko-pdp-summary__submit [name="add"] span')
        if (submitLabel) submitLabel.textContent = this.addButtonLabel
      }

      bindDiscountCode() {
        const widget = this.querySelector('[data-discount-code]')
        const copyButton = widget?.querySelector('[data-discount-code-copy]')
        const codeElement = widget?.querySelector('[data-discount-code-value]')
        const copyLabel = copyButton?.querySelector('[data-discount-code-copy-label]')
        const status = widget?.querySelector('[data-discount-code-status]')

        if (!copyButton || !codeElement || !copyLabel) return

        copyButton.addEventListener('click', async () => {
          const code = codeElement.textContent.trim()
          if (!code) return

          try {
            await this.copyText(code)
            copyLabel.textContent = copyButton.dataset.copiedLabel || 'Copied'
            if (status) status.textContent = copyButton.dataset.copiedLabel || 'Copied'

            clearTimeout(this.discountCodeResetTimer)
            this.discountCodeResetTimer = setTimeout(() => {
              copyLabel.textContent = copyButton.dataset.copyLabel || 'Copy'
              if (status) status.textContent = ''
            }, 2000)
          } catch (error) {
            if (status) status.textContent = 'Unable to copy code'
          }
        })
      }

      async copyText(text) {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          return
        }

        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.setAttribute('readonly', '')
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        const copied = document.execCommand('copy')
        textArea.remove()
        if (!copied) throw new Error('Copy failed')
      }

      bindMobileStickyCart() {
        this.mobileStickyCart = this.querySelector('[data-mobile-sticky-cart]')
        this.mainSubmitButton = this.querySelector('.nelko-pdp-summary__submit [name="add"]')
        this.mobileSubmitButton = this.querySelector('[data-mobile-sticky-submit]')
        this.mobileSubmitLabel = this.querySelector('[data-mobile-sticky-submit-label]')
        this.mobileSubmitLoader = this.mobileSubmitButton?.querySelector('.nelko-print-loader')

        if (!this.mobileStickyCart || !this.mainSubmitButton || !this.mobileSubmitButton) return

        // Reserve space after the footer without changing its existing padding.
        // Keep it reserved while hidden so visibility changes do not shift the page.
        this.stickyCartSpacer = document.createElement('div')
        this.stickyCartSpacer.setAttribute('aria-hidden', 'true')
        this.stickyCartSpacer.style.flexShrink = '0'
        document.body.append(this.stickyCartSpacer)
        const updateStickyCartSpace = () => {
          this.stickyCartSpacer.style.height = `${this.mobileStickyCart.offsetHeight}px`
        }
        updateStickyCartSpace()
        this.stickyCartResizeObserver = new ResizeObserver(updateStickyCartSpace)
        this.stickyCartResizeObserver.observe(this.mobileStickyCart)

        this.mobileSubmitButton.addEventListener('click', () => {
          if (
            this.mobileSubmitButton.disabled ||
            this.mobileSubmitButton.getAttribute('aria-disabled') === 'true'
          ) return
          this.mainSubmitButton.click()
        })

        this.mobileSubmitObserver = new MutationObserver(() => this.syncMobileSubmitButton())
        this.mobileSubmitObserver.observe(this.mainSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        })

        if ('IntersectionObserver' in window) {
          this.mobileStickyVisibilityObserver = new IntersectionObserver(
            ([entry]) => {
              this.mobileStickyCart.classList.toggle('is-visible', !entry.isIntersecting)
            },
            { threshold: 0 }
          )
          this.mobileStickyVisibilityObserver.observe(this.mainSubmitButton)
        } else {
          this.mobileStickyCart.classList.add('is-visible')
        }

        this.syncMobileSubmitButton()
      }

      syncMobileSubmitButton() {
        if (!this.mainSubmitButton || !this.mobileSubmitButton) return

        const isLoading = this.mainSubmitButton.classList.contains('loading')
        const isDisabled =
          this.mainSubmitButton.disabled ||
          this.mainSubmitButton.getAttribute('aria-disabled') === 'true' ||
          isLoading
        const mainLabel = this.mainSubmitButton.querySelector('span:not(.sold-out-message):not(.svg-wrapper)')
        const soldOutLabel = this.mainSubmitButton.querySelector('.sold-out-message:not(.hidden)')

        this.mobileSubmitButton.disabled = this.mainSubmitButton.disabled
        this.mobileSubmitButton.classList.toggle('loading', isLoading)
        this.mobileSubmitLoader?.classList.toggle('hidden', !isLoading)
        this.mobileSubmitButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')

        if (this.mobileSubmitLabel) {
          const label = this.currentVariant && !this.currentVariant.available
            ? this.mobileSubmitButton.dataset.soldOutLabel
            : soldOutLabel?.textContent.trim() ||
              mainLabel?.textContent.trim() ||
              this.mobileSubmitButton.dataset.addLabel
          if (label) this.mobileSubmitLabel.textContent = label
        }
      }

      bindQuantity() {
        if (!this.quantityInput) return
        this.quantityInput.addEventListener('change', this.updateTotals.bind(this))
        this.quantityInput.addEventListener('input', this.updateTotals.bind(this))
      }

      bindDelivery() {
        this.deliveryTrigger?.addEventListener('click', this.openDeliveryDialog.bind(this))
        this.deliveryForm?.addEventListener('submit', this.handleDeliverySubmit.bind(this))
        this.deliveryDetect?.addEventListener('click', this.handleDeliveryDetectClick.bind(this))

        this.querySelectorAll('[data-delivery-close]').forEach((button) => {
          button.addEventListener('click', this.closeDeliveryDialog.bind(this))
        })
      }

      applySavedDeliveryPostcode() {
        const savedPostcode = this.getStoredDeliveryPostcode()
        if (savedPostcode) {
          this.updateDeliveryPostcode(savedPostcode, false)
          return
        }

        if (this.defaultPostcode) {
          this.updateDeliveryPostcode(this.defaultPostcode, false)
        }
      }

      detectInitialDeliveryPostcode() {
        if (this.getStoredDeliveryPostcode()) return
        if (!this.deliveryLookupEndpoint) return
        this.detectDeliveryPostcode(false)
      }

      openDeliveryDialog() {
        if (!this.deliveryDialog) return

        this.deliveryDialog.hidden = false
        document.addEventListener('keydown', this.handleDeliveryKeydownBound)

        if (this.deliveryInput && this.deliveryValue) {
          this.deliveryInput.value = this.deliveryValue.textContent.trim()
        }

        window.setTimeout(() => {
          if (this.deliveryInput) {
            this.deliveryInput.focus()
            this.deliveryInput.select()
            return
          }
          this.deliveryPanel?.focus()
        }, 0)
      }

      closeDeliveryDialog() {
        if (!this.deliveryDialog) return

        this.deliveryDialog.hidden = true
        document.removeEventListener('keydown', this.handleDeliveryKeydownBound)
        this.setDeliveryStatus('')
        this.deliveryTrigger?.focus()
      }

      handleDeliveryKeydown(event) {
        if (event.key === 'Escape') this.closeDeliveryDialog()
      }

      handleDeliverySubmit(event) {
        event.preventDefault()

        const postcode = this.normalizeDeliveryPostcode(this.deliveryInput?.value || '')
        if (!postcode) {
          this.setDeliveryStatus(this.deliveryUnavailableText)
          return
        }

        this.updateDeliveryPostcode(postcode, true)
        this.closeDeliveryDialog()
      }

      async handleDeliveryDetectClick() {
        await this.detectDeliveryPostcode(true)
      }

      async detectDeliveryPostcode(showStatus) {
        if (!this.deliveryLookupEndpoint) return

        if (showStatus) this.setDeliveryStatus(this.deliveryDetectingText)

        try {
          const response = await fetch(this.deliveryLookupEndpoint, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          })

          if (!response.ok) throw new Error('Delivery lookup failed')

          const data = await response.json()
          const postcode = this.extractDeliveryPostcode(data)
          if (!postcode) throw new Error('Delivery postcode missing')

          this.updateDeliveryPostcode(postcode, true)
          this.setDeliveryStatus('')
        } catch (error) {
          if (showStatus) this.setDeliveryStatus(this.deliveryUnavailableText)
        }
      }

      extractDeliveryPostcode(data) {
        if (!data || typeof data !== 'object') return ''
        return this.normalizeDeliveryPostcode(
          data.postal_code || data.postalCode || data.zip || data.postcode || data.postal || ''
        )
      }

      updateDeliveryPostcode(postcode, shouldStore) {
        const normalizedPostcode = this.normalizeDeliveryPostcode(postcode)
        if (!normalizedPostcode) return

        if (this.deliveryValue) this.deliveryValue.textContent = normalizedPostcode
        if (this.deliveryInput) this.deliveryInput.value = normalizedPostcode
        if (shouldStore) this.storeDeliveryPostcode(normalizedPostcode)
      }

      normalizeDeliveryPostcode(value) {
        return String(value || '').trim().slice(0, 16)
      }

      getStoredDeliveryPostcode() {
        try {
          return this.normalizeDeliveryPostcode(window.localStorage.getItem(this.deliveryStorageKey))
        } catch (error) {
          return ''
        }
      }

      storeDeliveryPostcode(postcode) {
        try {
          window.localStorage.setItem(this.deliveryStorageKey, postcode)
        } catch (error) {
          console.error(error)
        }
      }

      setDeliveryStatus(message) {
        if (this.deliveryStatus) this.deliveryStatus.textContent = message || ''
      }

      updateTotals() {
        if (!this.currentVariant) return

        const quantity = this.quantityInput ? parseInt(this.quantityInput.value || '1', 10) : 1
        const totalPrice = this.currentVariant.price * quantity
        const totalCompare = this.currentVariant.compare_at_price
          ? this.currentVariant.compare_at_price * quantity
          : 0
        const onSale = totalCompare > totalPrice

        const totalPriceNodes = this.querySelectorAll('[data-total-price], [data-mobile-sticky-total]')
        const totalCompareNodes = this.querySelectorAll('[data-total-compare], [data-mobile-sticky-compare]')

        totalPriceNodes.forEach((node) => {
          node.textContent = this.formatMoney(totalPrice)
        })

        totalCompareNodes.forEach((node) => {
          node.textContent = onSale ? this.formatMoney(totalCompare) : ''
          node.classList.toggle('is-hidden', !onSale)
        })
      }

      formatMoney(amount) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(amount, this.moneyFormat)
        }

        return `$${(amount / 100).toFixed(2)}`
      }
    }
  )
}
